import React from 'react';
import './public/site.css';
import PublicHeader from './public/PublicHeader';
import PublicFooter from './public/PublicFooter';

// Icon components (same visuals as VN page)
const LeafIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = 'currentColor' }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Leaf">
		<g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<path d="M6 3c5.5 2.5 5.5 16.5 0 19" fill={color} opacity=".15" />
			<path d="M6 3c5.5 2.5 5.5 16.5 0 19" />
			<path d="M6 3c-2.5 5.5-2.5 10.5 0 19" />
		</g>
	</svg>
);

const WaterIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = 'currentColor' }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Water">
		<g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill={color} opacity=".15" />
			<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
		</g>
	</svg>
);

const WarningIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = 'currentColor' }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Warning">
		<g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" fill={color} opacity=".15" />
			<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
			<line x1="12" y1="9" x2="12" y2="13" />
			<line x1="12" y1="17" x2="12.01" y2="17" />
		</g>
	</svg>
);

const FlowerIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = 'currentColor' }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Flower">
		<g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<circle cx="12" cy="12" r="3" fill={color} opacity=".15" />
			<circle cx="12" cy="12" r="3" />
			<path d="M12 1v6m0 6v6M1 12h6m6 0h6" />
			<path d="M12 1c-2.5 2.5-2.5 6.5 0 9s6.5 2.5 9 0 2.5-6.5 0-9-6.5-2.5-9 0z" />
		</g>
	</svg>
);

const BasketIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = 'currentColor' }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Basket">
		<g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<path d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-6" fill={color} opacity=".15" />
			<path d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-6" />
		</g>
	</svg>
);

const CutIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = 'currentColor' }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Cut">
		<g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<circle cx="6" cy="6" r="3" fill={color} opacity=".15" />
			<circle cx="6" cy="6" r="3" />
			<circle cx="18" cy="18" r="3" fill={color} opacity=".15" />
			<circle cx="18" cy="18" r="3" />
			<line x1="6" y1="18" x2="18" y2="6" />
			<path d="M6 6l12 12" />
		</g>
	</svg>
);

const LaoDongNongNghiepEN: React.FC = () => {
	React.useEffect(() => {
		document.title = 'Agricultural labor - Fconnect';
	}, []);

	return (
		<div className="home-page fc-site">
			<PublicHeader lang="en" />

			{/* Banner Section */}
			<section className="ldt-banner-section">
				<div className="ldt-banner-content">
					<div className="ldt-banner-text">
						<h2>Professional agricultural labor services</h2>
						<p>Garden and crop care with modern techniques</p>
					</div>
					<div className="ldt-banner-visual">
						<div className="ldt-banner-icons">
							<LeafIcon size={48} />
							<WaterIcon size={48} />
							<FlowerIcon size={48} />
							<BasketIcon size={48} />
						</div>
					</div>
				</div>
			</section>

			{/* Content */}
			<section className="home-container">
				<header className="ldt-hero">
					<h1>Agricultural labor</h1>
					<p className="ldt-subtitle">Professional, trustworthy, high-quality in agriculture and plant care.</p>
				</header>

				<div className="ldt-content">
					<section className="ldt-section">
						<h2 className="section-title">Services</h2>
						<div className="services-grid">
							<div className="service-card">
								<div className="service-icon"><LeafIcon size={56} /></div>
								<h4>Mowing</h4>
								<p>Remove weeds and keep your garden and yard neat.</p>
							</div>
							<div className="service-card">
								<div className="service-icon"><WaterIcon size={56} /></div>
								<h4>Fertilizing</h4>
								<p>Apply the right fertilizer and dosage for optimal growth.</p>
							</div>
							<div className="service-card">
								<div className="service-icon"><WarningIcon size={56} /></div>
								<h4>Pest control</h4>
								<p>Safe and effective plant protection following proper procedures.</p>
							</div>
							<div className="service-card">
								<div className="service-icon"><FlowerIcon size={56} /></div>
								<h4>Planting</h4>
								<p>Sowing, nurturing seedlings, and creating optimal environments.</p>
							</div>
							<div className="service-card">
								<div className="service-icon"><BasketIcon size={56} /></div>
								<h4>Harvesting</h4>
								<p>Harvest at the right time for quality and productivity.</p>
							</div>
							<div className="service-card">
								<div className="service-icon"><CutIcon size={56} /></div>
								<h4>Garden care</h4>
								<p>Comprehensive garden care, trimming, and shaping plants.</p>
							</div>
						</div>
					</section>

					<section className="ldt-section">
						<h2 className="section-title">Hourly packages</h2>
						<div className="packages-grid">
							<div className="package-card">
								<div className="package-header">
									<h3>2-hour package</h3>
									<p className="package-sub">Best for light tasks</p>
								</div>
								<ul className="package-list">
									<li>Mow small areas</li>
									<li>Basic fertilizing</li>
									<li>Plant health check</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>3-hour package</h3>
									<p className="package-sub">Complete one task</p>
								</div>
								<ul className="package-list">
									<li>Detailed garden care</li>
									<li>Fertilizing and watering</li>
									<li>Trimming plants</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>4-hour package</h3>
									<p className="package-sub">Multiple tasks in one visit</p>
								</div>
								<ul className="package-list">
									<li>Full garden cleanup</li>
									<li>Fertilizing and pest control</li>
									<li>New planting and care</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>Full-day package</h3>
									<p className="package-sub">All-day service for bigger needs</p>
								</div>
								<ul className="package-list">
									<li>Comprehensive garden care</li>
									<li>New planting and maintenance</li>
									<li>Harvesting and cleanup</li>
								</ul>
							</div>
						</div>
					</section>
				</div>
			</section>

			<PublicFooter lang="en" />
		</div>
	);
};

export default LaoDongNongNghiepEN;


